/**
 * =============================================================================
 * panelTheme.js — EVERY value the Interpretation panel draws with
 * =============================================================================
 *
 * THE PANEL INHERITS NOTHING. Not a colour, not a size, not an opacity, not a
 * font. `ProblemCard` and `InterpretationPanel` import this and read no other
 * source — no `useTheme`, no `TIER_COLORS`, no CSS variables from <body>.
 *
 * WHY THAT MATTERS: the panel is a distinct object inside the page, not a
 * region of it. Anything it inherited would be a value someone could change
 * from outside without knowing the panel existed, and the panel would silently
 * change with it. Editing every colour and every transparency has to be
 * possible HERE, in one file, with no hunting.
 *
 * SO: if a component needs a value, it goes in this file first. A literal in a
 * style object is a bug — it is a value that cannot be found from here.
 *
 * ── NO LIGHT VARIANT, ON PURPOSE ───────────────────────────────────────────
 * Free is the only light tier and free never sees the panel — nor the orrery or
 * the error box, which follow the same PANEL_TIERS list. (The requests still run
 * for free; only the boxes are hidden.) Only pro_plus (`#0D0D0D`) sees them for
 * now; pro (`#1A1A1A`) will again later. Both dark, so ONE palette covers every
 * case the panel can appear in. Adding an `isLight` branch now would be two code
 * paths with one unreachable.
 * =============================================================================
 */

// ── SURFACES ────────────────────────────────────────────────────────────────
// TWO NESTED ONES. A wrapper in `bg` holding a card in `surface`. The card
// floating on a slightly different background is most of the effect, and it is
// the easiest thing to lose when rebuilding.
const bg      = '#1c1b1a';   // the outer wrapper
const surface = '#242322';   // the card itself
const sunken  = '#151414';   // inside a field, and the track of a loading bar

// ── INK ─────────────────────────────────────────────────────────────────────
const text      = '#ece9e5';  // transcribed lines — the thing being checked
const secondary = '#b3aea8';  // instructions, the task line, supporting copy
const muted     = '#7d7873';  // hints, the dash, the delete glyph

// ── THE SECTION BLUE ────────────────────────────────────────────────────────
// ONE HUE FOR EVERY HEADING AND EVERY FIELD. Not three.
//
// Colour-coding the roles was tried and abandoned: on a maths card green means
// CORRECT, and a student whose working was outlined green would reasonably
// conclude it had been checked and passed. Amber says the same in reverse. The
// sections are told apart by their headings, their indent and the gap between
// them — which is how the display view already worked before any colour.
const blue       = '#7FA8E8';
const blueBorder = 'rgba(127, 168, 232, 0.34)';
const blueBg     = 'rgba(127, 168, 232, 0.12)';

// ── THE GOLD ────────────────────────────────────────────────────────────────
// Resubmit, and the live toggle. The only filled thing on the card, because it
// is the only action that sends anything.
const accent     = '#C89B6A';
const accentBg   = 'rgba(200, 155, 106, 0.14)';
const accentText = '#D9B183';   // gold as TEXT needs lifting, or it muddies

// ── THE GREEN ───────────────────────────────────────────────────────────────
// `edit` only. It opens something rather than sending it, and the colour is
// what says so. It never touches a transcribed line, so it cannot be read as a
// verdict on the student's work.
const successText   = '#8FCB9B';
const successBg     = 'rgba(143, 203, 155, 0.12)';
const successBorder = 'rgba(143, 203, 155, 0.28)';

export const PANEL = {
  bg, surface, sunken,
  text, secondary, muted,

  // HAIRLINES AT 0.5px, not full-strength rules.
  border:       'rgba(255, 255, 255, 0.10)',
  borderStrong: 'rgba(255, 255, 255, 0.22)',

  blue, blueBorder, blueBg,
  accent, accentBg, accentText,
  successText, successBg, successBorder,

  serif: 'Georgia, "Times New Roman", serif',
  sans:  'system-ui, -apple-system, sans-serif',

  // ── THE PANEL'S COLUMN ────────────────────────────────────────────────────
  // Read by `SearchResultsPage`, which owns the container the cards sit in.
  // They live at the top level rather than under `layout` because that file
  // reads them as PANEL.maxWidth — moving them broke it silently, since a
  // missing key is `undefined` rather than an error, and the container
  // collapsed to `maxWidth: undefined` with `padding: 0 undefinedpx`.
  maxWidth: 640,
  padX: 24,
  padXMobile: 16,

  // ── TYPE ──────────────────────────────────────────────────────────────────
  size: {
    title:       15,     // "Problem 42"
    heading:     10.5,   // INTERPRETATION, INSTRUCTIONS, PROBLEM …
    hint:        12,
    line:        16,     // a transcribed line, display
    lineMobile:  15,
    instruction: 14.5,
    field:       14,     // a transcribed line, edit
    role:        11,     // the role dropdown
    control:     11.5,   // + line, + instruction, edit
    resubmit:    13,
    badge:       10.5,
    toggle:      12,
  },

  // ── LAYOUT ────────────────────────────────────────────────────────────────
  //
  // THE RIGHT EDGE IS THE HARD PART, and every value here is a FIXED INSET from
  // the card's content edge — never a percentage.
  //
  // A percentage of an indented container resolves somewhere else than the same
  // percentage of its parent, so a 79% clip inside the 10px level and a 79%
  // clip inside the 20px level end at two different places. Fixed insets cannot
  // drift.
  layout: {
    cardPadX:       17,  // 1.05rem
    cardPadY:       14,
    cardPadMobile:  14,  // 0.9rem, both axes

    // THREE LEVELS. `Interpretation` and `Answer` at 0, `Instructions` at 10,
    // the three roles at 20. The step only has to be legible, not generous — a
    // heading starting 10px right of the one above is unmistakably nested.
    indentSection:  10,
    indentRole:     20,
    indentMobileSection: 0,
    indentMobileRole:    10,
    indentBody:     10,  // content, in from its own heading

    // R − gutter: where EVERY editable field ends. 14px glyph + 6px gap.
    gutter:     20,
    deleteSize: 14,

    // R − notes: where instructions stop in display. Stopping short is what
    // keeps them looking like notes; a line running to the edge reads as a
    // paragraph however it is punctuated.
    notes:       140,
    notesMobile: 44,

    // The worked steps are a quotation INSIDE the card, not more of the card.
    // Pulling them in from both edges is what says so, without a box or a rule
    // around the block. Also what gives the centred equations room to sit on
    // one axis without touching the sides.
    stepsInset:       26,
    stepsInsetMobile: 12,

    roleWidth:       108,  // the role dropdown
    roleWidthMobile:  84,
    fieldHeight:      30,
    radius:            6,
    cardRadius:       12,
    gap:               6,
    rowGap:            6,
  },

  // Below this the inline editor row leaves the field too narrow to see an
  // expression in, and the labels shorten from "Your work" to "Work".
  narrowAt: 560,
};

export default PANEL;
